import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import VisaTabs from "../Visas/VisaTabs";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import jsonData from "../../Data/b2bvisas.json";

const initialFieldValues = {
  supplierVisaPriceId: "00000000-0000-0000-0000-000000000000",
  supplierId: "00000000-0000-0000-0000-000000000000",
  visaId: "",
  adultPrice: 0,
  childPrice: 0,
};

const SupplierVisaPrices = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [loadingStates, setLoadingStates] = useState({});
  const [lists, setLists] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [bulkPrices, setBulkPrices] = useState({});
  const [selectedLetter, setSelectedLetter] = useState("ALL");
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [supplierLoading, setSupplierLoading] = useState(false);

  // Function to get visa details by visaId from JSON data
  const getVisaDetails = (visaId) => {
    for (const country of jsonData) {
      const visa = country.visas.find((v) => v.visaId === visaId);
      if (visa) {
        return {
          ...visa,
          countryName: country.name,
          flagSrc: country.flagSrc,
          countryCode: country.code,
        };
      }
    }
    return null;
  };

  // Function to find existing price record for a visa
  const findExistingPriceRecord = (visaId) => {
    return lists.find((item) => item.visaId === visaId);
  };

  // Initialize bulk prices for all countries and visas
  const initializeAllBulkPrices = () => {
    const initialBulkPrices = {};

    jsonData.forEach((country) => {
      country.visas.forEach((visa) => {
        const existingRecord = findExistingPriceRecord(visa.visaId);

        initialBulkPrices[visa.visaId] = {
          supplierVisaPriceId: existingRecord
            ? existingRecord.supplierVisaPriceId
            : "00000000-0000-0000-0000-000000000000",
          adultPrice: existingRecord ? existingRecord.adultPrice : "",
          childPrice: existingRecord ? existingRecord.childPrice : "",
          isExisting: !!existingRecord,
          countryCode: country.code,
          countryName: country.name,
          flagSrc: country.flagSrc,
          visaName: visa.visaName,
        };
      });
    });

    setBulkPrices(initialBulkPrices);
  };

  // Get suppliers from API
  const getSuppliers = async () => {
    setSupplierLoading(true);
    try {
      const response = await apiService.getAll(
        `${config.GETALLCOMPANIES}?companyType=supplier`
      );
      const suppliersData = response.data || response;
      setSuppliers(suppliersData);

      // Auto-select first supplier if available
      if (suppliersData.length > 0 && !selectedSupplier) {
        setSelectedSupplier(suppliersData[0].companyId);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setSupplierLoading(false);
    }
  };

  const handleOpenDeleteModal = (visaId) => {
    setItemToDelete(visaId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleBulkPriceChange = (visaId, field, value) => {
    setBulkPrices((prev) => ({
      ...prev,
      [visaId]: {
        ...prev[visaId],
        [field]: value === "" ? "" : parseFloat(value) || 0,
      },
    }));
  };

  // Get unique first letters from ALL country names (including those without visas)
  const getAlphabetLetters = () => {
    const letters = new Set();
    jsonData.forEach((country) => {
      const firstLetter = country.name.charAt(0).toUpperCase();
      letters.add(firstLetter);
    });

    const sortedLetters = Array.from(letters).sort();
    return ["ALL", ...sortedLetters];
  };

  // Get all countries grouped by first letter (including those without visas)
  const getCountriesByLetter = () => {
    const grouped = {};

    jsonData.forEach((country) => {
      const firstLetter = country.name.charAt(0).toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(country);
    });

    // Sort countries alphabetically within each letter group
    Object.keys(grouped).forEach((letter) => {
      grouped[letter].sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  };

  // Filter countries by selected letter and only include those with visas
  const getFilteredCountriesWithVisas = () => {
    let countriesToFilter = [];

    if (selectedLetter === "ALL") {
      countriesToFilter = jsonData.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const countriesByLetter = getCountriesByLetter();
      countriesToFilter = countriesByLetter[selectedLetter] || [];
    }

    // Only return countries that have visas
    return countriesToFilter.filter(
      (country) => country.visas && country.visas.length > 0
    );
  };

  // Get ALL filtered countries (including those without visas) for the alphabet navigation
  const getAllFilteredCountries = () => {
    if (selectedLetter === "ALL") {
      return jsonData.sort((a, b) => a.name.localeCompare(b.name));
    }

    const countriesByLetter = getCountriesByLetter();
    return countriesByLetter[selectedLetter] || [];
  };

  const GetData = async () => {
    if (!selectedSupplier) return;

    try {
      const res = await apiService.getAll(
        `${config.GETSUPPLIERVISAPRICESBYSUPPLIERID}/${selectedSupplier}`
      );

      // If res is an object with data property, use res.data, otherwise use res directly
      const data = res.data || res;

      setLists(data);

      // Initialize bulk prices after getting data
      initializeAllBulkPrices();
    } catch (error) {}
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        // Find the record to delete
        const recordToDelete = lists.find(
          (item) => item.visaId === itemToDelete
        );
        if (recordToDelete) {
          await apiService.remove(
            config.DELETESUPPLIERVISAPRICE,
            recordToDelete.supplierVisaPriceId
          );
        }

        // Reset the values to empty in the form
        if (bulkPrices[itemToDelete]) {
          setBulkPrices((prev) => ({
            ...prev,
            [itemToDelete]: {
              ...prev[itemToDelete],
              adultPrice: "",
              childPrice: "",
              isExisting: false,
              supplierVisaPriceId: "00000000-0000-0000-0000-000000000000",
            },
          }));
        }

        await GetData();
      } catch (error) {}
      handleCloseDeleteModal();
    }
  };

  const handleSingleUpdate = async (visaId, priceData) => {
    if (!selectedSupplier) {
      alert("Please select a supplier first");
      return;
    }

    if (
      !priceData.adultPrice ||
      priceData.adultPrice <= 0 ||
      !priceData.childPrice ||
      priceData.childPrice <= 0
    ) {
      alert("Please enter valid prices for both adult and child");
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [visaId]: true }));
    try {
      const formData = {
        supplierVisaPriceId: priceData.supplierVisaPriceId,
        supplierId: selectedSupplier,
        visaId: visaId,
        adultPrice: priceData.adultPrice,
        childPrice: priceData.childPrice,
      };

      // Use UPDATE for existing records, CREATE for new ones
      if (priceData.isExisting) {
        await apiService.update(config.UPDATESUPPLIERVISAPRICE, formData);
      } else {
        await apiService.create(config.CREATESUPPLIERVISAPRICE, formData);
      }

      await GetData();

      // Refresh the bulk prices data to reflect the update
      const updatedBulkPrices = { ...bulkPrices };
      updatedBulkPrices[visaId] = {
        ...priceData,
        isExisting: true, // Mark as existing after successful creation/update
      };
      setBulkPrices(updatedBulkPrices);
    } catch (error) {
      alert("Error updating price. Please try again.");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [visaId]: false }));
    }
  };

  // Get country count for each letter (including countries without visas)
  const getCountryCountByLetter = () => {
    const countriesByLetter = getCountriesByLetter();
    const counts = {};

    Object.keys(countriesByLetter).forEach((letter) => {
      counts[letter] = countriesByLetter[letter].length;
    });

    counts["ALL"] = jsonData.length;

    return counts;
  };

  // Get country count with visas for each letter
  const getCountryWithVisasCountByLetter = () => {
    const countriesByLetter = getCountriesByLetter();
    const counts = {};

    Object.keys(countriesByLetter).forEach((letter) => {
      counts[letter] = countriesByLetter[letter].filter(
        (country) => country.visas && country.visas.length > 0
      ).length;
    });

    counts["ALL"] = jsonData.filter(
      (country) => country.visas && country.visas.length > 0
    ).length;

    return counts;
  };

  // Get price data for a specific visa
  const getPriceDataForVisa = (visaId) => {
    const priceData = bulkPrices[visaId];

    return (
      priceData || {
        supplierVisaPriceId: "00000000-0000-0000-0000-000000000000",
        adultPrice: "",
        childPrice: "",
        isExisting: false,
        countryCode: "",
        countryName: "",
        flagSrc: "",
        visaName: "",
      }
    );
  };

  // Handle supplier change
  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
    // Reset lists when supplier changes
    setLists([]);
    setBulkPrices({});
  };

  useEffect(() => {
    if (user && !loading) {
      getSuppliers();
    }
  }, [user, loading]);

  // Load data when supplier changes
  useEffect(() => {
    if (selectedSupplier) {
      GetData();
    }
  }, [selectedSupplier]);

  // Re-initialize bulk prices when lists change
  useEffect(() => {
    if (lists.length > 0) {
      initializeAllBulkPrices();
    }
  }, [lists]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  const alphabetLetters = getAlphabetLetters();
  const filteredCountriesWithVisas = getFilteredCountriesWithVisas();
  const allFilteredCountries = getAllFilteredCountries();
  const countryCounts = getCountryCountByLetter();
  const countryWithVisasCounts = getCountryWithVisasCountByLetter();
  const countriesByLetter = getCountriesByLetter();

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <VisaTabs pageName="SUPPLIERS VISA PRICES" />
                </div>

                {/* Supplier Dropdown */}
                <div className="card-body border-bottom">
                  <div className="row">
                    <div className="col-3">
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          Select Supplier:
                        </label>
                        <select
                          className="form-select"
                          value={selectedSupplier}
                          onChange={handleSupplierChange}
                          disabled={supplierLoading}
                        >
                          <option value="">Select a supplier</option>
                          {suppliers.map((supplier) => (
                            <option
                              key={supplier.companyId}
                              value={supplier.companyId}
                            >
                              {supplier.companyName} ({supplier.brandName})
                            </option>
                          ))}
                        </select>
                        {supplierLoading && (
                          <div className="form-text">Loading suppliers...</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alphabet Navigation */}
                <div className="card-body border-bottom">
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                        <label className="form-label mb-0 fw-semibold">
                          Filter by Country:
                        </label>
                        {alphabetLetters.map((letter) => (
                          <button
                            key={letter}
                            type="button"
                            className={`btn btn-sm ${
                              selectedLetter === letter
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setSelectedLetter(letter)}
                            style={{ minWidth: "50px" }}
                            title={`${letter} - ${
                              countryWithVisasCounts[letter] || 0
                            } countries with visas`}
                          >
                            {letter}
                            <span className="ms-1 badge bg-light text-dark">
                              {countryWithVisasCounts[letter] || 0}
                            </span>
                          </button>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="text-muted">
                            Showing {filteredCountriesWithVisas.length}{" "}
                            countries with visas
                            {selectedLetter !== "ALL" &&
                              ` starting with "${selectedLetter}"`}
                            {allFilteredCountries.length >
                              filteredCountriesWithVisas.length && (
                              <span className="text-warning ms-1">
                                (
                                {allFilteredCountries.length -
                                  filteredCountriesWithVisas.length}{" "}
                                countries without visas hidden)
                              </span>
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="badge bg-primary">
                            Total Visas:{" "}
                            {filteredCountriesWithVisas.reduce(
                              (total, country) => total + country.visas.length,
                              0
                            )}
                          </span>
                        </div>
                      </div>
                      {/* Note Message */}
                      <div className="card-body border-bottom bg-info bg-opacity-10">
                        <div className="alert alert-danger mb-0">
                          <i className="ri-information-line me-2"></i>
                          <strong>Note:</strong> After entering prices, click
                          the "Submit" button to save your changes.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Country Quick Links - Show ALL countries under selected letter (including those without visas) */}
                {selectedLetter !== "ALL" &&
                  countriesByLetter[selectedLetter] && (
                    <div className="card-body border-bottom bg-light">
                      <div className="row">
                        <div className="col-12">
                          <h6 className="mb-3">
                            All countries starting with '{selectedLetter}':
                            <span className="badge bg-secondary ms-2">
                              {countriesByLetter[selectedLetter].length} total
                            </span>
                            <span className="badge bg-success ms-1">
                              {countryWithVisasCounts[selectedLetter]} with
                              visas
                            </span>
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {countriesByLetter[selectedLetter].map(
                              (country) => {
                                const hasVisas =
                                  country.visas && country.visas.length > 0;
                                return (
                                  <button
                                    key={country.code}
                                    type="button"
                                    className={`btn btn-sm ${
                                      hasVisas
                                        ? "btn-outline-primary"
                                        : "btn-outline-secondary"
                                    }`}
                                    onClick={() => {
                                      if (hasVisas) {
                                        // Scroll to the country section
                                        const element = document.getElementById(
                                          `country-${country.code}`
                                        );
                                        if (element) {
                                          element.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                          });
                                        }
                                      }
                                    }}
                                    disabled={!hasVisas}
                                    title={
                                      hasVisas
                                        ? `View ${country.visas.length} visas`
                                        : "No visas available"
                                    }
                                  >
                                    <img
                                      src={`https://flagcdn.com/w20/${country.flagSrc.toLowerCase()}.png`}
                                      alt={country.name}
                                      className="me-1"
                                      style={{
                                        width: "16px",
                                        height: "12px",
                                        opacity: hasVisas ? 1 : 0.5,
                                      }}
                                    />
                                    {country.name}
                                    <span
                                      className={`badge ms-1 ${
                                        hasVisas ? "bg-primary" : "bg-secondary"
                                      }`}
                                    >
                                      {country.visas.length}
                                    </span>
                                  </button>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Countries and Visas List - Only show countries that HAVE visas */}
                <div className="card-body">
                  {!selectedSupplier ? (
                    <div className="text-center py-4">
                      <p className="text-muted">
                        Please select a supplier to view and manage visa prices.
                      </p>
                    </div>
                  ) : filteredCountriesWithVisas.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">
                        {selectedLetter === "ALL"
                          ? "No countries with visas found."
                          : `No countries with visas found starting with "${selectedLetter}".`}
                      </p>
                      <p className="text-warning small">
                        Try selecting a different letter or check if countries
                        have visa data.
                      </p>
                    </div>
                  ) : (
                    <div className="visa-prices-list">
                      {filteredCountriesWithVisas.map(
                        (country, countryIndex) => {
                          return (
                            <div
                              key={country.code}
                              className="country-section mb-4"
                            >
                              {/* Country Header */}
                              <div
                                className="country-header bg-light p-3 rounded mb-3"
                                id={`country-${country.code}`}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={`https://flagcdn.com/w20/${country.flagSrc.toLowerCase()}.png`}
                                      alt={country.name}
                                      className="me-2"
                                      style={{ width: "24px", height: "18px" }}
                                    />
                                    <h5 className="mb-0 fw-bold">
                                      {country.name}
                                    </h5>
                                    <span className="badge bg-primary ms-2">
                                      {country.visas.length} visa types
                                    </span>
                                  </div>
                                  <div className="text-muted">
                                    Country {countryIndex + 1} of{" "}
                                    {filteredCountriesWithVisas.length}
                                  </div>
                                </div>
                              </div>

                              {/* Visas List */}
                              <div className="visas-list">
                                <div className="table-responsive">
                                  <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                      <tr>
                                        <th width="40%">Visa Type</th>
                                        <th width="20%">Adult Price (INR)</th>
                                        <th width="20%">Child Price (INR)</th>
                                        <th width="20%">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {country.visas.map((visa) => {
                                        const priceData = getPriceDataForVisa(
                                          visa.visaId
                                        );

                                        return (
                                          <tr key={visa.visaId}>
                                            <td>
                                              <strong>{visa.visaName}</strong>
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={
                                                  priceData.adultPrice || ""
                                                }
                                                onChange={(e) =>
                                                  handleBulkPriceChange(
                                                    visa.visaId,
                                                    "adultPrice",
                                                    e.target.value
                                                  )
                                                }
                                                className="form-control form-control-sm"
                                                placeholder="0.00"
                                              />
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={
                                                  priceData.childPrice || ""
                                                }
                                                onChange={(e) =>
                                                  handleBulkPriceChange(
                                                    visa.visaId,
                                                    "childPrice",
                                                    e.target.value
                                                  )
                                                }
                                                className="form-control form-control-sm"
                                                placeholder="0.00"
                                              />
                                            </td>
                                            <td>
                                              <div className="d-flex gap-2">
                                                <button
                                                  type="button"
                                                  className={`btn btn-sm ${
                                                    priceData.isExisting
                                                      ? "btn-primary"
                                                      : "btn-success"
                                                  }`}
                                                  onClick={() =>
                                                    handleSingleUpdate(
                                                      visa.visaId,
                                                      priceData
                                                    )
                                                  }
                                                  disabled={
                                                    loadingStates[visa.visaId]
                                                  }
                                                  style={{ minWidth: "80px" }}
                                                >
                                                  {loadingStates[visa.visaId]
                                                    ? "..."
                                                    : "Submit"}
                                                </button>

                                                {priceData.isExisting && (
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() =>
                                                      handleOpenDeleteModal(
                                                        visa.visaId
                                                      )
                                                    }
                                                    title="Delete Price"
                                                  >
                                                    <i className="ri-delete-bin-line" />
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Country Separator */}
                              {countryIndex <
                                filteredCountriesWithVisas.length - 1 && (
                                <hr className="my-4" />
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </div>
  );
};

export default SupplierVisaPrices;
